import React, { useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { Tooltip } from '@mui/material';

import * as S from './styles';
import Text from 'components/Text';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useMenuTitle } from 'hooks/useMenuTitleContext';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { useSideBar } from 'hooks/useSideBar';
import { Icon, MenuItem } from 'types/menuTypes';

interface IconData {
  url: string;
  name?: string;
}

interface IconProps {
  style: {
    width: string;
    height: string;
  };
}

interface IButtonSibBar {
  isActive?: boolean;
  path: string;
  subtitle?: string;
  icon: IconData | IconData[] | React.ComponentType<IconProps> | React.ReactNode;
  subMenu: MenuItem[];
  isSubMenu?: boolean;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  level: 'first' | 'second' | 'third';
}

const ButtonSidebar = ({
  isActive = false,
  path,
  subtitle = '',
  icon,
  subMenu = [],
  isSubMenu = false,
  label,
  onClick,
  disabled = false,
  level,
}: IButtonSibBar) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { open } = useSideBar();
  const { updateMenuTitle } = useMenuTitle();
  const {
    setFirstLevelMenu,
    setSecondLevelMenu,
    setThirdLevelMenu,
    selectMenuByDocumentId,
    firstLevelMenu,
    secondLevelMenu,
    thirdLevelMenu,
  } = useMenuSelection();

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(buttonRef, () => {
    setIsSubMenuOpen(false);
  });

  useEffect(() => {
    if (!open) {
      setIsSubMenuOpen(false);
    }
  }, [open]);

  // Função recursiva para verificar se um menu é descendente de outro
  function isDescendant(menu: MenuItem, targetPath: string): boolean {
    if (!menu.subMenu) {
      return false;
    }

    for (const sub of menu.subMenu) {
      if (sub.path === targetPath) {
        return true;
      }

      const recursiveResult = isDescendant(sub, targetPath);
      if (recursiveResult) {
        return true;
      }
    }

    return false;
  }

  // Função para verificar se um menu está ativo baseado na cascata de menus
  const isMenuActive = () => {
    // Se for um menu de primeiro nível
    if (level === 'first') {
      if (pathname === path) return true;
      if (subMenu.some(item => item.path === pathname)) return true;
      return subMenu.some(item => item.subMenu?.some(subItem => subItem.path === pathname));
    }

    // Se for um menu de segundo nível
    if (level === 'second') {
      // Verifica se o path do segundo nível é igual ao pathname
      if (pathname === path) {
        return true;
      }

      // Verifica se o segundo nível está selecionado no contexto
      if (secondLevelMenu && secondLevelMenu.path === path) {
        return true;
      }

      // Verifica se o terceiro nível atual é descendente deste segundo nível
      if (thirdLevelMenu) {
        const isDesc = isDescendant(
          {
            label,
            path,
            icon: icon as Icon,
            isActive: isActive,
            isMobile: false,
            rank: 0,
            feature: null,
            level,
            subMenu: subMenu,
          },
          thirdLevelMenu.path
        );

        if (isDesc) {
          return true;
        }
      }

      return false;
    }

    // Se for um menu de terceiro nível
    if (level === 'third') {
      if (pathname === path) return true;
      if (thirdLevelMenu && thirdLevelMenu.path === path) return true;
      return false;
    }

    return false;
  };

  const isActiveLocation = isMenuActive();

  const handleClick = () => {
    if (disabled) return;

    // Atualiza o contexto de menu apenas se necessário
    const menuItem: MenuItem = {
      label,
      path,
      icon: icon as Icon,
      isActive: true,
      isMobile: false,
      rank: 0,
      feature: null,
      level,
      subMenu: subMenu,
    };

    if (onClick) {
      onClick();
      return;
    }

    if (subMenu.length > 0) {
      setIsSubMenuOpen((prev: boolean) => !prev);
      return;
    }

    // Caso especial: segundo nível sem feature.documentId, mas com submenus
    if (level === 'second' && !menuItem.feature?.documentId && subMenu.length > 0) {
      const firstChild = subMenu[0];
      if (firstChild?.feature?.documentId) {
        // Sempre passa o path específico para garantir navegação correta
        selectMenuByDocumentId(firstChild.feature.documentId, true, undefined, firstChild.path);

        return;
      }
    }

    navigate(path);
    updateMenuTitle(label);

    if (level === 'first') {
      setFirstLevelMenu(menuItem);
      setSecondLevelMenu(null);
      setThirdLevelMenu(null);
    } else if ((level === 'second' || level === 'third') && menuItem.feature?.documentId) {
      // Sempre passa o path específico para garantir navegação correta
      selectMenuByDocumentId(menuItem.feature.documentId, true, undefined, path);
    }
  };

  const renderIcon = () => {
    if (!icon) return null;

    // Se o ícone for um array (como no caso do Cockpit)
    if (Array.isArray(icon)) {
      const iconData = icon[0]; // Pega o primeiro ícone do array
      if (iconData?.url) {
        return (
          <img
            src={iconData.url}
            alt={iconData.name || ''}
            style={{ width: '24px', height: '24px' }}
          />
        );
      }
      return null;
    }

    // Se for um objeto com url (como no caso dos submenus)
    if (typeof icon === 'object' && icon && 'url' in icon) {
      const iconData = icon as IconData;
      return (
        <img
          src={iconData.url}
          alt={iconData.name || ''}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }

    // Se for um elemento React válido
    if (React.isValidElement(icon)) {
      return icon;
    }

    // Se for um componente funcional
    if (typeof icon === 'function') {
      const IconComponent = icon as React.ComponentType<IconProps>;
      return <IconComponent style={{ width: '24px', height: '24px' }} />;
    }

    return null;
  };

  return (
    <S.Container
      open={open}
      isSelected={isActiveLocation}
      isActive={isActive}
      isDisabled={disabled}
      onClick={handleClick}
      isSubMenu={isSubMenu}
      ref={buttonRef}
    >
      {isActiveLocation && <S.Bar />}
      <Tooltip title={!open && label} placement="right-start" arrow>
        <S.Wrapper>
          {renderIcon()}
          {open && (
            <Text size="xSmall" bold={isActiveLocation} onClick={handleClick}>
              {label} {subtitle}
            </Text>
          )}
        </S.Wrapper>
      </Tooltip>
    </S.Container>
  );
};

export default ButtonSidebar;
